import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
} from "@material-ui/core";
import ForwardIcon from "@material-ui/icons/Forward";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch, useSelector } from "react-redux";
import OrderRes from "types/res/order/OrderRes";
import UpdateOrderReq from "types/req/order/UpdateOrderReq";
import { fetchOneOrder, updateOrder } from "redux/actions/order";
import OrderMessagePanel, {
  OrderMessagePanelHandle,
} from "pages/Order/components/OrderMessagePanel";
import DateTimeUtils, { FORMAT_TYPE } from "utils/DateTimeUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  order: OrderRes | null;
  newDriverId: number | null;
  driverType: "outward" | "return";
}

/**
 * #477 (5/29): 配車画面 D&D 後の統合モーダル。
 * 旧 DriverScheduleOnItemMove (確認 + duration + メール送信チェック) と
 * 新 OrderMessagePanel (テンプレ + 送信) を 1 つの Dialog で完結。
 * - 「保存のみ」 : updateOrder (アサイン保存) のみ実行 → close
 * - 「保存して送信」 : updateOrder + OrderMessagePanel.send() を atomic 実行 → fetchOrder で赤色即時反映 → close
 * - 未アサイン (ドライバー null) も選択可能 (空 MenuItem)
 */
const DriverDispatchSendDialog: React.FC<Props> = ({
  open,
  onClose,
  order,
  newDriverId,
  driverType,
}) => {
  const dispatch = useDispatch();
  const driverShifts = useSelector((state) => state.driverShift);
  const companyId = useSelector(
    (state: { account: { staff: { companyId: number } } }) =>
      state.account.staff.companyId
  );
  const [selectDriverId, setSelectDriverId] = useState<number | "">("");
  const [duration, setDuration] = useState<number | undefined>();
  const [busy, setBusy] = useState<"none" | "save" | "send">("none");
  const orderMessageRef = useRef<OrderMessagePanelHandle>(null);

  useEffect(() => {
    if (!order) return;
    // 未アサイン (null) も明示的に「空」 として扱う
    setSelectDriverId(newDriverId ?? "");
    if (driverType === "return") {
      setDuration(order.returnDuration ?? 30);
    } else {
      setDuration(order.outwardDuration ?? 30);
    }
  }, [order, newDriverId, driverType]);

  const buildOrderForUpdate = () => {
    if (!order) return null;
    const orderClone: any = { ...order };
    orderClone.options =
      orderClone.options?.map((o: any) => o.optionId) || [];
    orderClone.discounts =
      orderClone.discounts?.map((d: any) => d.discountId) || [];
    const driverIdForSave = selectDriverId === "" ? null : selectDriverId;
    if (driverType === "return") {
      orderClone.returnDriverId = driverIdForSave;
      orderClone.returnDuration = duration;
    } else {
      orderClone.outwardDriverId = driverIdForSave;
      orderClone.outwardDuration = duration;
    }
    return orderClone;
  };

  const onSaveOnly = async () => {
    const orderClone = buildOrderForUpdate();
    if (!orderClone) return;
    setBusy("save");
    try {
      await dispatch(updateOrder(companyId, new UpdateOrderReq(orderClone)));
      onClose();
    } finally {
      setBusy("none");
    }
  };

  const onSaveAndSend = async () => {
    const orderClone = buildOrderForUpdate();
    if (!orderClone) return;
    setBusy("send");
    try {
      // 1. アサイン保存
      await dispatch(updateOrder(companyId, new UpdateOrderReq(orderClone)));
      // 2. OrderMessagePanel.send() (内部 confirm → SMS/LINE/メール送信、 onSent で fetchOrder)
      if (orderMessageRef.current) {
        await orderMessageRef.current.send();
      }
      onClose();
    } finally {
      setBusy("none");
    }
  };

  if (!order) return null;
  const oldDriverId =
    driverType === "return" ? order.returnDriverId : order.outwardDriverId;
  const targetLabel = driverType === "return" ? "迎え" : "送り";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography>
        <Typography variant="h6" style={{ display: "inline-block" }}>
          送迎変更 + ドライバー送信 ({targetLabel})
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
          size="small"
          disabled={busy !== "none"}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* 受注情報 1 行サマリ (圧縮) */}
        <Typography
          variant="caption"
          style={{
            display: "block",
            backgroundColor: "#f5f5f5",
            padding: 6,
            borderRadius: 4,
            marginBottom: 8,
          }}
        >
          <strong>店舗:</strong> {order.shop?.name || "-"}
          　<strong>顧客:</strong> {order.guest?.name || "-"}
          　<strong>源氏名:</strong> {order.castName?.name || "-"}
          　<strong>出発:</strong>{" "}
          {order.departureTime
            ? DateTimeUtils.toFormatAsLocalTimezone(
                order.departureTime,
                FORMAT_TYPE.TIME
              )
            : "-"}
          　<strong>予定IN:</strong>{" "}
          {order.planInTime
            ? DateTimeUtils.toFormatAsLocalTimezone(
                order.planInTime,
                FORMAT_TYPE.TIME
              )
            : "-"}
          　<strong>予定OUT:</strong>{" "}
          {order.planOutTime
            ? DateTimeUtils.toFormatAsLocalTimezone(
                order.planOutTime,
                FORMAT_TYPE.TIME
              )
            : "-"}
        </Typography>

        {/* アサイン UI 1 行コンパクト (未アサイン選択可) */}
        <Box display="flex" mb={1} alignItems="flex-end" style={{ gap: 8 }}>
          <Box flex={1}>
            <Typography variant="caption" color="textSecondary">
              変更前
            </Typography>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              disabled
              value={oldDriverId ?? ""}
            >
              <MenuItem value="">未アサイン</MenuItem>
              {driverShifts.map((ds: any) => (
                <MenuItem key={ds.driverId} value={ds.driverId}>
                  {ds.driver?.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <ForwardIcon style={{ marginBottom: 6 }} />
          <Box flex={1}>
            <Typography variant="caption" color="textSecondary">
              変更後
            </Typography>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              value={selectDriverId}
              onChange={(e) => {
                const v = e.target.value as string;
                setSelectDriverId(v === "" ? "" : Number(v));
              }}
            >
              <MenuItem value="">未アサイン</MenuItem>
              {driverShifts.map((ds: any) => (
                <MenuItem key={ds.driverId} value={ds.driverId}>
                  {ds.driver?.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box style={{ width: 110 }}>
            <Typography variant="caption" color="textSecondary">
              所要(分)
            </Typography>
            <TextField
              type="number"
              fullWidth
              variant="outlined"
              size="small"
              value={duration ?? ""}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          </Box>
        </Box>

        {/* メッセージ送信 (OrderMessagePanel embed、 driver タブ + 送信先固定、 compactRows でファーストビューに送信方法を入れる) */}
        <OrderMessagePanel
          ref={orderMessageRef}
          orderId={order.orderId}
          initialTab="driver"
          initialDriverTarget={driverType}
          hideSendButton
          compactRows={5}
          onSent={() => {
            // 送信成功 → 該当 order だけ refetch (配車画面の赤色即時反映)
            if (order) dispatch(fetchOneOrder(companyId, order.orderId));
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy !== "none"}>
          キャンセル
        </Button>
        <Button
          onClick={onSaveOnly}
          disabled={busy !== "none"}
          startIcon={
            busy === "save" ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {busy === "save" ? "保存中..." : "保存のみ"}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={onSaveAndSend}
          disabled={busy !== "none"}
          startIcon={
            busy === "send" ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {busy === "send" ? "処理中..." : "保存して送信"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverDispatchSendDialog;
